import { useEffect, useState } from "react";
import { Plus, ListPlus, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { listMyPlaylists, createPlaylist, addToPlaylist } from "@/lib/playlists.functions";

interface Playlist { id: string; title: string; is_public: boolean }

export function AddToPlaylistButton({ activationId }: { activationId: string }) {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<Playlist[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const fetchLists = useServerFn(listMyPlaylists);
  const create = useServerFn(createPlaylist);
  const add = useServerFn(addToPlaylist);

  useEffect(() => { supabase.auth.getUser().then(({ data }) => setSignedIn(!!data.user)); }, []);

  async function load() {
    const data = await fetchLists();
    setLists(data as Playlist[]);
  }

  useEffect(() => { if (open && !lists) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  if (signedIn === false) return null;

  async function addTo(id: string) {
    setBusy(id);
    try { await add({ data: { playlist_id: id, activation_id: activationId } }); setOpen(false); }
    finally { setBusy(null); }
  }

  async function createAndAdd() {
    if (!newTitle.trim()) return;
    setBusy("new");
    try {
      const pl = await create({ data: { title: newTitle.trim(), is_public: false } });
      await add({ data: { playlist_id: (pl as { id: string }).id, activation_id: activationId } });
      setNewTitle("");
      setOpen(false);
    } finally { setBusy(null); }
  }

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} aria-label="Add to playlist"
        className="inline-flex h-11 w-11 items-center justify-center rounded-full border hairline hover:border-gold hover:text-gold">
        <ListPlus size={16} />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-72 rounded-xl border hairline bg-obsidian p-3 shadow-xl">
          <p className="px-1 pb-2 text-xs uppercase tracking-[0.25em] text-gold">Add to playlist</p>
          <div className="max-h-56 overflow-y-auto">
            {lists === null && <p className="p-2 text-xs text-muted-foreground">Loading…</p>}
            {lists?.length === 0 && <p className="p-2 text-xs text-muted-foreground">No playlists yet.</p>}
            {lists?.map((l) => (
              <button key={l.id} onClick={() => addTo(l.id)} disabled={!!busy}
                className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm hover:bg-charcoal">
                <span>{l.title}</span>
                {busy === l.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              </button>
            ))}
          </div>
          <div className="mt-2 flex gap-2 border-t hairline pt-2">
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New playlist name"
              className="flex-1 rounded border hairline bg-obsidian px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gold" />
            <button onClick={createAndAdd} disabled={!!busy || !newTitle.trim()}
              className="rounded border hairline px-3 text-xs uppercase tracking-widest hover:border-gold hover:text-gold disabled:opacity-50">
              {busy === "new" ? "…" : "Add"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
